import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Briefcase, ArrowLeft, UserPlus, Sparkles, Loader2, Crown, Check, Lock, GraduationCap, TrendingUp } from "lucide-react";
import html2canvas from "html2canvas";
import EducationRequirements, { EDUCATION_CONFIG } from "@/components/EducationRequirements";

// Helper function to format education for display (same as RequisitionBuilder)
const formatEducationForDisplay = (education) => {
  if (!education) return "";
  if (typeof education === "string") return education;
  
  if (education.track === "general_academic") {
    const getDegreeLabel = (degreeValue) => {
      return EDUCATION_CONFIG.general_academic.higherDegrees.find(d => d.value === degreeValue)?.label || degreeValue;
    };
    
    const reqFields = education.required?.fields || [];
    const prefFields = education.preferred?.fields || [];
    const allUniqueFields = [...new Set([...reqFields, ...prefFields])];
    
    const allDescriptions = allUniqueFields
      .filter(f => f !== "related_field")
      .map(f => {
        const field = EDUCATION_CONFIG.general_academic.fields.find(field => field.value === f);
        return field?.description || "";
      })
      .filter(d => d);
    
    const formatDegreeBase = (degreeValue, fields) => {
      const degreeLabel = getDegreeLabel(degreeValue);
      const primaryFields = fields.filter(f => f !== "related_field");
      const hasRelatedField = fields.includes("related_field");
      
      const primaryLabels = primaryFields.map(f => {
        const field = EDUCATION_CONFIG.general_academic.fields.find(field => field.value === f);
        return field?.label || f;
      });
      
      let fieldText = primaryLabels.join(", ");
      if (hasRelatedField) {
        fieldText += " or Related Field";
      }
      
      return `${degreeLabel} in ${fieldText}`;
    };
    
    const parts = [];
    
    const requiredBases = [];
    if (education.required?.hsGed) {
      requiredBases.push("High School/GED");
    }
    if (education.required?.degree && education.required.degree !== "none" && reqFields.length > 0) {
      requiredBases.push(formatDegreeBase(education.required.degree, reqFields));
    }
    if (requiredBases.length > 0) {
      parts.push(requiredBases.join(" + ") + " required");
    }
    
    if (education.preferred?.degree && education.preferred.degree !== "none" && prefFields.length > 0) {
      parts.push(formatDegreeBase(education.preferred.degree, prefFields) + " preferred");
    }
    
    let result = parts.join("; ");
    if (allDescriptions.length > 0) {
      result += ` (${allDescriptions.join("; ")})`;
    }
    
    return result;
  } else if (education.track === "healthcare_clinical") {
    const getCredLabel = (credKey) => {
      const [catKey, credValue] = credKey.split(":");
      const category = EDUCATION_CONFIG.healthcare_clinical.categories[catKey];
      const cred = category?.credentials.find(c => c.value === credValue);
      return cred?.label || credValue;
    };
    
    const requiredParts = [];
    
    if (education.required?.hsGed) {
      requiredParts.push("High School/GED");
    }
    
    if (education.required?.credentials?.length > 0) {
      requiredParts.push(education.required.credentials.map(getCredLabel).join(", "));
    }
    
    const parts = [];
    if (requiredParts.length > 0) {
      parts.push(requiredParts.join(" + ") + " required");
    }
    
    if (education.preferred?.credentials?.length > 0) {
      parts.push(education.preferred.credentials.map(getCredLabel).join(", ") + " preferred");
    }
    return parts.join("; ");
  }
  
  return "";
};

export default function TryRequisition() {
  const navigate = useNavigate();
  const outputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    requisition_name: "",
    job_title: "",
    company_brand: "",
    basic_qualifications: "",
    preferred_qualifications: "",
    education: null, // Now stores structured education object
    years_experience: "",
    location: "",
    job_type: "",
    industry: "",
    tone: "",
    company_culture: "",
    additional_context: "",
    travel: "",
    relocation_provided: "",
    us_citizenship: [],
    security_clearance: "",
    shift_coverage: "",
    benefits_offered: []
  });
  
  const [generatedData, setGeneratedData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [outputImage, setOutputImage] = useState(null);
  const [isCapturing, setIsCapturing] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [progressStatus, setProgressStatus] = useState({ percent: 0, message: "" });
  const [showPremiumModal, setShowPremiumModal] = useState(false);

  // Disable keyboard shortcuts for copying when output is shown
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (outputImage) {
        // Block Ctrl+C, Ctrl+S, Ctrl+P, Ctrl+Shift+S (copy, save, print, save as)
        if ((e.ctrlKey || e.metaKey) && ['c', 's', 'p'].includes(e.key.toLowerCase())) {
          e.preventDefault();
          toast.info("Sign up to save and export your requisitions!", {
            action: {
              label: "Sign Up",
              onClick: () => navigate("/pricing")
            }
          });
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [outputImage, navigate]);

  // Trigger image capture when data is generated
  useEffect(() => {
    if (generatedData && !outputImage && !isCapturing) {
      setIsCapturing(true);
    }
  }, [generatedData, outputImage, isCapturing]);

  // Capture the output as image once DOM is ready
  useEffect(() => {
    if (isCapturing && outputRef.current && generatedData && !outputImage) {
      const timer = setTimeout(async () => {
        try {
          const canvas = await html2canvas(outputRef.current, {
            scale: 2,
            backgroundColor: "#ffffff",
            logging: false,
            useCORS: true,
            allowTaint: true,
            windowHeight: outputRef.current.scrollHeight,
          });
          
          const imageUrl = canvas.toDataURL("image/png");
          setOutputImage(imageUrl);
          setIsCapturing(false);
          toast.success("Requisition ready!");
        } catch (error) {
          console.error("Image conversion error:", error);
          setIsCapturing(false);
          toast.error("Failed to convert to image");
        }
      }, 1000);
      
      return () => clearTimeout(timer);
    }
  }, [isCapturing, generatedData, outputImage]);

  const handleGenerate = async () => {
    if (!formData.job_title || !formData.location) {
      toast.error("Please fill in at least Job Title and Location");
      return;
    }

    setLoading(true);
    setShowPreview(false);
    setProgressStatus({ percent: 0, message: "" });
    
    try {
      // Progress updates
      setProgressStatus({ percent: 10, message: "Initializing AI analysis..." });
      await new Promise(resolve => setTimeout(resolve, 300));
      
      setProgressStatus({ percent: 20, message: "Analyzing job requirements..." });
      await new Promise(resolve => setTimeout(resolve, 300));
      
      // Guest users don't have enhanced research - always use standard processing
      setProgressStatus({ percent: 35, message: "Processing input data..." });
      await new Promise(resolve => setTimeout(resolve, 300));
      
      setProgressStatus({ percent: 60, message: "Generating requisition content..." });
      await new Promise(resolve => setTimeout(resolve, 400));
      
      setProgressStatus({ percent: 80, message: "Fetching salary data..." });
      
      // Format education for API
      const educationString = formData.education ? formatEducationForDisplay(formData.education) : "";
      
      const params = new URLSearchParams({
        title: formData.job_title,
        location: formData.location,
        department: formData.company_brand || "General",
        description: formData.additional_context || "",
        enhanced_research: "false",  // Always false for guest users
        industry: formData.industry || "",  // Pass selected industry
        education: educationString,
        years_experience: formData.years_experience || "",
      });

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/requisitions/generate-guest?${params}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
        }
      );

      if (!response.ok) throw new Error("Generation failed");

      const data = await response.json();
      console.log("Generated data received:", data);
      console.log("Has description:", !!data.description);
      console.log("Has requirements:", data.requirements?.length || 0);
      console.log("Has skills:", data.skills?.length || 0);
      console.log("Has preferred_skills:", data.preferred_skills?.length || 0);
      console.log("Has market_data:", !!data.market_data);
      
      setProgressStatus({ percent: 95, message: "Preparing output..." });
      setGeneratedData(data);
      
      setProgressStatus({ percent: 100, message: "Complete!" });
      await new Promise(resolve => setTimeout(resolve, 500));
      setProgressStatus({ percent: 0, message: "" });
      
      toast.success("Requisition generated! Preparing image...");
    } catch (error) {
      toast.error("Failed to generate requisition. Please try again.");
      setProgressStatus({ percent: 0, message: "" });
    } finally {
      setLoading(false);
    }
  };

  const handlePreview = async () => {
    if (!formData.job_title || !formData.location) {
      toast.error("Please fill in at least Job Title and Location");
      return;
    }

    setLoading(true);
    setShowPreview(true);
    setProgressStatus({ percent: 0, message: "" });
    
    try {
      setProgressStatus({ percent: 20, message: "Creating preview..." });
      await new Promise(resolve => setTimeout(resolve, 300));
      
      setProgressStatus({ percent: 60, message: "Generating content..." });
      
      // Format education for API
      const educationString = formData.education ? formatEducationForDisplay(formData.education) : "";
      
      const params = new URLSearchParams({
        title: formData.job_title,
        location: formData.location,
        department: formData.company_brand || "General",
        description: formData.additional_context || "",
        enhanced_research: "false",
        industry: formData.industry || "",  // Pass selected industry
        education: educationString,
        years_experience: formData.years_experience || "",
      });

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/requisitions/generate-guest?${params}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
        }
      );

      if (!response.ok) throw new Error("Preview failed");

      const data = await response.json();
      
      setProgressStatus({ percent: 95, message: "Preparing output..." });
      setGeneratedData(data);
      
      setProgressStatus({ percent: 100, message: "Complete!" });
      await new Promise(resolve => setTimeout(resolve, 500));
      setProgressStatus({ percent: 0, message: "" });
      
      toast.success("Preview generated! Preparing image...");
    } catch (error) {
      toast.error("Failed to generate preview. Please try again.");
      setProgressStatus({ percent: 0, message: "" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Briefcase className="w-8 h-8 text-blue-900" />
            <div>
              <h1 className="text-xl font-bold text-slate-900">SmartReqs - Try It Out!</h1>
              <p className="text-xs text-slate-600">CaioAbruzzo LLC</p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate("/")}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Button>
            <Button
              size="sm"
              onClick={() => navigate("/pricing")}
            >
              <UserPlus className="w-4 h-4 mr-2" />
              Create Account
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="mb-6 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <Sparkles className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm text-blue-900 mb-2">
                <strong>Try SmartReqs for free!</strong> Generate a sample job requisition to see how our AI works. 
                No account required. To save and manage requisitions, please create an account.
              </p>
              <div className="bg-white/60 rounded-md p-3 mt-2 border border-blue-100">
                <p className="text-xs text-blue-800 mb-2">
                  <strong className="text-blue-900">💎 Premium Members Get Enhanced Salary Intelligence:</strong>
                </p>
                <ul className="text-xs text-blue-800 space-y-1 ml-4 list-disc">
                  <li><strong>BLS Government Data</strong> - Official Bureau of Labor Statistics salary benchmarks</li>
                  <li><strong>Experience-Adjusted Salaries</strong> - Multipliers based on years of experience (entry, mid-level, senior)</li>
                  <li><strong>Multi-Source Validation</strong> - Cross-referenced data from 5+ job board APIs</li>
                  <li><strong>Location Cost-of-Living</strong> - Automatic adjustments for 200+ metro areas</li>
                  <li><strong>Security Clearance Premiums</strong> - 25% salary boost for TS/SCI positions</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 min-h-[calc(100vh-200px)]">
          {/* Left Column - Input Form */}
          <div className="flex flex-col">
            <Card>
              <CardHeader>
                <CardTitle>Job Requisition Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="job_title">Job Title</Label>
                  <Input
                    id="job_title"
                    value={formData.job_title}
                    onChange={(e) => setFormData({ ...formData, job_title: e.target.value })}
                    placeholder="e.g., Senior Software Engineer"
                  />
                </div>

                <div>
                  <Label htmlFor="requisition_name">Requisition Name</Label>
                  <Input
                    id="requisition_name"
                    value={formData.requisition_name}
                    onChange={(e) => setFormData({ ...formData, requisition_name: e.target.value })}
                    placeholder="e.g., Senior Engineer - Bay Area Q4 2025"
                  />
                </div>

                <div>
                  <Label htmlFor="company_brand">Company Brand</Label>
                  <Input
                    id="company_brand"
                    value={formData.company_brand}
                    onChange={(e) => setFormData({ ...formData, company_brand: e.target.value })}
                    placeholder="e.g., Acme Inc."
                  />
                </div>

                <div>
                  <Label htmlFor="basic_qualifications">Basic Qualifications</Label>
                  <Textarea
                    id="basic_qualifications"
                    value={formData.basic_qualifications}
                    onChange={(e) => setFormData({ ...formData, basic_qualifications: e.target.value })}
                    placeholder="e.g., 5 years experience, required skills & experience, minimum certification(s), etc."
                    rows={3}
                  />
                </div>

                <div>
                  <Label htmlFor="preferred_qualifications">Preferred Qualifications</Label>
                  <Textarea
                    id="preferred_qualifications"
                    value={formData.preferred_qualifications}
                    onChange={(e) => setFormData({ ...formData, preferred_qualifications: e.target.value })}
                    placeholder="e.g., Ideal skills & relevant experience, proficiencies, desired certifications, etc."
                    rows={3}
                  />
                </div>

                {/* Education Requirements - New Enhanced Component */}
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <GraduationCap className="w-4 h-4 text-slate-600" />
                    <Label className="text-sm font-medium">Education Requirements</Label>
                  </div>
                  <EducationRequirements
                    value={formData.education}
                    onChange={(educationData) => setFormData({ ...formData, education: educationData })}
                  />
                </div>

                <div>
                  <Label htmlFor="years_experience">Years of Experience</Label>
                  <Input
                    id="years_experience"
                    value={formData.years_experience}
                    onChange={(e) => setFormData({ ...formData, years_experience: e.target.value })}
                    placeholder="e.g., 3-5 years"
                  />
                </div>

                <div>
                  <Label htmlFor="location">Location</Label>
                  <Input
                    id="location"
                    value={formData.location}
                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                    placeholder="e.g., San Francisco, CA or Remote"
                  />
                </div>

                <div>
                  <Label htmlFor="industry">Industry</Label>
                  <Select
                    value={formData.industry}
                    onValueChange={(value) => setFormData({ ...formData, industry: value })}
                  >
                    <SelectTrigger id="industry">
                      <SelectValue placeholder="Select industry" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Technology/Software">Technology/Software</SelectItem>
                      <SelectItem value="Healthcare/Biotech">Healthcare/Biotech</SelectItem>
                      <SelectItem value="Finance/Banking">Finance/Banking</SelectItem>
                      <SelectItem value="Manufacturing/Industrial">Manufacturing/Industrial</SelectItem>
                      <SelectItem value="Aerospace/Defense">Aerospace/Defense</SelectItem>
                      <SelectItem value="Retail/E-commerce">Retail/E-commerce</SelectItem>
                      <SelectItem value="Education">Education</SelectItem>
                      <SelectItem value="Government/Public Sector">Government/Public Sector</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="tone">Tone & Style</Label>
                  <Select
                    value={formData.tone}
                    onValueChange={(value) => setFormData({ ...formData, tone: value })}
                  >
                    <SelectTrigger id="tone">
                      <SelectValue placeholder="Select writing tone" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Professional">Professional (formal, corporate)</SelectItem>
                      <SelectItem value="Innovative">Innovative (dynamic, forward-thinking)</SelectItem>
                      <SelectItem value="Collaborative">Collaborative (team-focused)</SelectItem>
                      <SelectItem value="Entrepreneurial">Entrepreneurial (startup-oriented)</SelectItem>
                      <SelectItem value="Production">Production (deliverable-driven)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="company_culture">Company Culture</Label>
                  <Textarea
                    id="company_culture"
                    value={formData.company_culture}
                    onChange={(e) => setFormData({ ...formData, company_culture: e.target.value })}
                    placeholder="Describe your company culture, values, and work environment..."
                    rows={3}
                  />
                </div>

                <div>
                  <Label htmlFor="additional_context">Additional Context: Suggestions Below</Label>
                  <Textarea
                    id="additional_context"
                    value={formData.additional_context}
                    onChange={(e) => setFormData({ ...formData, additional_context: e.target.value })}
                    placeholder="Any specific requirements, team details, or context..."
                    rows={4}
                  />
                </div>

                <div>
                  <Label htmlFor="travel">Travel</Label>
                  <Select
                    value={formData.travel}
                    onValueChange={(value) => setFormData({ ...formData, travel: value })}
                  >
                    <SelectTrigger id="travel">
                      <SelectValue placeholder="Select travel requirement" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="No travel">No travel</SelectItem>
                      <SelectItem value="Up to 25%">Up to 25%</SelectItem>
                      <SelectItem value="Up to 50%">Up to 50%</SelectItem>
                      <SelectItem value="Up to 75%">Up to 75%</SelectItem>
                      <SelectItem value="Up to 100%">Up to 100%</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Additional Details Section */}
                <Card className="bg-slate-50">
                  <CardHeader className="pb-3">
                    <Label className="text-sm font-medium">Additional Details</Label>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <Label htmlFor="relocation_provided">Relocation Provided</Label>
                      <Select
                        value={formData.relocation_provided}
                        onValueChange={(value) => setFormData({ ...formData, relocation_provided: value })}
                      >
                        <SelectTrigger id="relocation_provided">
                          <SelectValue placeholder="Select option" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Yes">Yes</SelectItem>
                          <SelectItem value="No">No</SelectItem>
                          <SelectItem value="Negotiable">Negotiable</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label>US Citizenship (Check all that apply)</Label>
                      <div className="space-y-2 mt-2">
                        {["None", "U.S. Citizen", "U.S. permanent resident (i.e. Green Card holder)", "Lawfully admitted into U.S. as a refugee or granted asylum"].map((option) => {
                          const currentCitizenship = formData.us_citizenship || [];
                          const noneSelected = currentCitizenship.includes("None");
                          const othersSelected = currentCitizenship.some(item => item !== "None");
                          const isDisabled = (option === "None" && othersSelected) || (option !== "None" && noneSelected);
                          
                          return (
                            <div key={option} className="flex items-center space-x-2">
                              <input
                                type="checkbox"
                                id={`citizenship-${option}`}
                                checked={currentCitizenship.includes(option)}
                                disabled={isDisabled}
                                onChange={(e) => {
                                  const newCitizenship = e.target.checked
                                    ? [...currentCitizenship, option]
                                    : currentCitizenship.filter(item => item !== option);
                                  setFormData({ ...formData, us_citizenship: newCitizenship });
                                }}
                                className={`w-4 h-4 rounded border-gray-300 ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                              />
                              <label 
                                htmlFor={`citizenship-${option}`} 
                                className={`text-sm cursor-pointer ${isDisabled ? 'text-slate-400' : 'text-slate-700'}`}
                              >
                                {option}
                              </label>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="security_clearance">Security Clearance Requirements</Label>
                      <Select
                        value={formData.security_clearance}
                        onValueChange={(value) => setFormData({ ...formData, security_clearance: value })}
                      >
                        <SelectTrigger id="security_clearance">
                          <SelectValue placeholder="Select clearance level" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="None">None Required</SelectItem>
                          <SelectItem value="Confidential">Confidential</SelectItem>
                          <SelectItem value="Secret">Secret</SelectItem>
                          <SelectItem value="Top Secret">Top Secret</SelectItem>
                          <SelectItem value="TS/SCI">TS/SCI</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label htmlFor="job_type">Job Type</Label>
                      <Select
                        value={formData.job_type}
                        onValueChange={(value) => setFormData({ ...formData, job_type: value })}
                      >
                        <SelectTrigger id="job_type">
                          <SelectValue placeholder="Select job type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Salaried">Salaried</SelectItem>
                          <SelectItem value="Hourly">Hourly</SelectItem>
                          <SelectItem value="Part Time">Part Time</SelectItem>
                          <SelectItem value="Contract">Contract</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label htmlFor="shift_coverage">Shift Coverage</Label>
                      <Select
                        value={formData.shift_coverage}
                        onValueChange={(value) => setFormData({ ...formData, shift_coverage: value })}
                      >
                        <SelectTrigger id="shift_coverage">
                          <SelectValue placeholder="Select shift" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="First Shift">First Shift (Day)</SelectItem>
                          <SelectItem value="Second Shift">Second Shift (Evening)</SelectItem>
                          <SelectItem value="Third Shift">Third Shift (Night)</SelectItem>
                          <SelectItem value="Rotating">Rotating Shifts</SelectItem>
                          <SelectItem value="Flexible">Flexible</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label>Benefits Offered (Check all that apply)</Label>
                      <div className="space-y-2 mt-2">
                        {["Paid time off", "Paid holidays", "9/80 Work week", "Paid parental leave", "Health insurance", "Health savings account", "Vision insurance", "Dental insurance", "Life insurance", "Disability insurance", "401(k) matching", "Stock Purchase Plan", "Tuition reimbursement"].map((benefit) => (
                          <div key={benefit} className="flex items-center space-x-2">
                            <input
                              type="checkbox"
                              id={`benefit-${benefit}`}
                              checked={(formData.benefits_offered || []).includes(benefit)}
                              onChange={(e) => {
                                const currentBenefits = formData.benefits_offered || [];
                                const newBenefits = e.target.checked
                                  ? [...currentBenefits, benefit]
                                  : currentBenefits.filter(item => item !== benefit);
                                setFormData({ ...formData, benefits_offered: newBenefits });
                              }}
                              className="w-4 h-4 rounded border-gray-300"
                            />
                            <label htmlFor={`benefit-${benefit}`} className="text-sm text-slate-700 cursor-pointer">
                              {benefit}
                            </label>
                          </div>
                        ))}
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <div className="space-y-3">
                  {/* Enhanced Research - Premium Feature */}
                  <div 
                    className="flex items-center justify-between p-4 bg-gradient-to-r from-amber-50 to-yellow-50 border border-amber-200 rounded-lg cursor-pointer hover:from-amber-100 hover:to-yellow-100 transition-colors"
                    onClick={() => setShowPremiumModal(true)}
                  >
                    <div className="flex items-center space-x-3">
                      <div className="relative">
                        <input
                          type="checkbox"
                          id="enhanced-research-guest"
                          checked={false}
                          readOnly
                          className="w-4 h-4 text-gray-400 border-gray-300 rounded cursor-pointer"
                        />
                        <Lock className="w-3 h-3 text-amber-600 absolute -top-1 -right-1" />
                      </div>
                      <div>
                        <label className="font-semibold text-sm text-amber-900 cursor-pointer flex items-center gap-2">
                          <Crown className="w-4 h-4 text-amber-500" />
                          Enhanced Research
                          <span className="text-xs bg-amber-200 text-amber-800 px-2 py-0.5 rounded-full">Premium</span>
                        </label>
                      </div>
                    </div>
                    <button
                      type="button"
                      className="text-amber-600 hover:text-amber-800"
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowPremiumModal(true);
                      }}
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </button>
                  </div>

                  {/* Progress Indicator */}
                  {loading && progressStatus.message && (
                    <div className="p-4 bg-white border border-blue-200 rounded-lg space-y-2">
                      <div className="flex items-center justify-between text-sm">
                        <span className="font-medium text-blue-900">{progressStatus.message}</span>
                        <span className="text-blue-600 font-semibold">{progressStatus.percent}%</span>
                      </div>
                      <Progress value={progressStatus.percent} className="h-2" />
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-2">
                    <Button
                      onClick={handlePreview}
                      disabled={loading || !formData.job_title || !formData.location}
                      variant="outline"
                      className="w-full"
                    >
                      {loading && showPreview ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Previewing...
                        </>
                      ) : (
                        "Preview"
                      )}
                    </Button>
                    
                    <Button
                      onClick={handleGenerate}
                      disabled={loading || !formData.job_title || !formData.location}
                      className="w-full"
                    >
                      {loading && !showPreview ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          AI Working...
                        </>
                      ) : (
                        <>
                          <Sparkles className="w-4 h-4 mr-2" />
                          Generate with AI
                        </>
                      )}
                    </Button>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-2 text-xs text-slate-600">
                    <div className="px-2 py-2 bg-slate-50 rounded border border-slate-200">
                      <p className="font-medium text-slate-700 mb-1">Preview</p>
                      <p>Quick draft generation without market analysis. Use to test different inputs.</p>
                    </div>
                    <div className="px-2 py-2 bg-blue-50 rounded border border-blue-200">
                      <p className="font-medium text-blue-700 mb-1">Generate with AI</p>
                      <p>Full generation with competitive intelligence and market salary analysis.</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Output */}
          <div className="flex flex-col min-h-[calc(100vh-200px)]">
            {/* Hidden div for html2canvas capture */}
            {generatedData && isCapturing && (
              <div style={{ position: 'absolute', left: '-10000px', width: '800px' }}>
                <div ref={outputRef} className="bg-white p-6" style={{ width: '800px' }}>
                  <Card className="shadow-none border-0">
                <CardHeader>
                  <CardTitle>
                    <span className="font-bold">Requisition Name: </span>
                    {formData.requisition_name || "Generated Requisition"}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4" data-testid="generated-content">
                  <div>
                    <p className="text-slate-900">
                      <span className="font-bold">Job Title: </span>
                      {formData.job_title}
                    </p>
                  </div>

                  {formData.company_brand && (
                    <div>
                      <p className="text-slate-900">
                        <span className="font-bold">Company Brand: </span>
                        {formData.company_brand}
                      </p>
                    </div>
                  )}

                  {generatedData.description && (
                    <div>
                      <h3 className="font-semibold text-sm text-slate-600 mb-1">Description</h3>
                      <p className="text-sm text-slate-700 whitespace-pre-wrap">{generatedData.description}</p>
                    </div>
                  )}

                  {generatedData.requirements && generatedData.requirements.length > 0 && (
                    <div>
                      <h3 className="font-semibold text-sm text-slate-600 mb-2">Key Responsibilities</h3>
                      <ul className="list-disc list-outside space-y-2 pl-6">
                        {generatedData.requirements.filter(req => req && req.trim() && req !== '**').map((req, i) => (
                          <li key={i} className="text-sm text-slate-700 break-words">{req}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {generatedData.skills && generatedData.skills.length > 0 && (
                    <div>
                      <h3 className="font-semibold text-sm text-slate-600 mb-2">Required Skills</h3>
                      <ul className="list-disc list-outside space-y-2 pl-6">
                        {generatedData.skills.filter(skill => skill && skill.trim() && skill !== '**').map((skill, i) => (
                          <li key={i} className="text-sm text-slate-700 break-words">{skill}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {generatedData.preferred_skills && generatedData.preferred_skills.length > 0 && (
                    <div>
                      <h3 className="font-semibold text-sm text-slate-600 mb-2">Preferred Skills</h3>
                      <ul className="list-disc list-outside space-y-2 pl-6">
                        {generatedData.preferred_skills.filter(skill => skill && skill.trim() && skill !== '**').map((skill, i) => (
                          <li key={i} className="text-sm text-slate-700 break-words">{skill}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {generatedData.experience_years && (
                    <div>
                      <h3 className="font-semibold text-sm text-slate-600 mb-1">Experience</h3>
                      <p className="text-sm text-slate-700">{generatedData.experience_years}</p>
                    </div>
                  )}

                  {generatedData.salary_range && (
                    <div>
                      <h3 className="font-semibold text-sm text-slate-600 mb-1">Salary Range</h3>
                      <p className="text-sm text-slate-700">{generatedData.salary_range}</p>
                    </div>
                  )}

                  {formData.travel && (
                    <div>
                      <h3 className="font-semibold text-sm text-slate-600 mb-1">Travel</h3>
                      <p className="text-sm text-slate-700">{formData.travel}</p>
                    </div>
                  )}

                  {formData.location && (
                    <div>
                      <h3 className="font-semibold text-sm text-slate-600 mb-1">Location</h3>
                      <p className="text-sm text-slate-700">{formData.location}</p>
                    </div>
                  )}

                  {/* Additional Details Section */}
                  {(formData.relocation_provided || formData.us_citizenship?.length > 0 || formData.security_clearance || formData.shift_coverage || formData.benefits_offered?.length > 0) && (
                    <div>
                      <h3 className="font-semibold text-sm text-slate-600 mb-2">Additional Details</h3>
                      <div className="space-y-1">
                        {formData.relocation_provided && (
                          <p className="text-sm text-slate-700">
                            <span className="font-semibold">Relocation:</span> {formData.relocation_provided}
                          </p>
                        )}
                        {formData.us_citizenship && formData.us_citizenship.length > 0 && (
                          <p className="text-sm text-slate-700">
                            <span className="font-semibold">US Citizenship:</span> {formData.us_citizenship.join(", ")}
                          </p>
                        )}
                        {formData.security_clearance && (
                          <p className="text-sm text-slate-700">
                            <span className="font-semibold">Security Clearance:</span> {formData.security_clearance}
                          </p>
                        )}
                        {formData.shift_coverage && (
                          <p className="text-sm text-slate-700">
                            <span className="font-semibold">Shift:</span> {formData.shift_coverage}
                          </p>
                        )}
                        {formData.benefits_offered && formData.benefits_offered.length > 0 && (
                          <p className="text-sm text-slate-700">
                            <span className="font-semibold">Benefits Offered:</span> {formData.benefits_offered.join(", ")}
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Competitive Intelligence Section - Above Market Analysis */}
                  {generatedData.competitive_intelligence && (
                    <div className="pt-4 border-t">
                      <h3 className="font-semibold text-sm text-slate-900 mb-3">Competitive Intelligence</h3>
                      <p className="text-xs text-slate-600 mb-3">Insights from similar job postings in the market</p>
                      
                      {/* Top In-Demand Skills */}
                      {generatedData.competitive_intelligence.in_demand_skills && generatedData.competitive_intelligence.in_demand_skills.length > 0 && (
                        <div className="mb-4">
                          <h4 className="font-semibold text-xs text-slate-900 mb-2">Top In-Demand Skills</h4>
                          <div className="flex flex-wrap gap-2">
                            {generatedData.competitive_intelligence.in_demand_skills.map((skill, i) => (
                              <span key={i} className="text-xs px-2 py-1 bg-blue-50 text-blue-700 rounded-full border border-blue-200">
                                {skill}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {/* Common Requirements */}
                      {generatedData.competitive_intelligence.common_requirements && generatedData.competitive_intelligence.common_requirements.length > 0 && (
                        <div className="mb-4">
                          <h4 className="font-semibold text-xs text-slate-900 mb-2">Common Requirements</h4>
                          <ul className="list-disc list-inside space-y-1">
                            {generatedData.competitive_intelligence.common_requirements.map((req, i) => (
                              <li key={i} className="text-xs text-slate-700">{req}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      
                      {/* Typical Qualifications */}
                      {generatedData.competitive_intelligence.typical_qualifications && (
                        <div className="mb-4">
                          <h4 className="font-semibold text-xs text-slate-900 mb-2">Typical Qualifications</h4>
                          <p className="text-xs text-slate-700">{generatedData.competitive_intelligence.typical_qualifications}</p>
                        </div>
                      )}
                      
                      {/* Competitive Salary Range */}
                      {generatedData.competitive_intelligence.competitive_salary && (
                        <div className="mb-4">
                          <h4 className="font-semibold text-xs text-slate-900 mb-2">Competitive Salary Range</h4>
                          <p className="text-xs text-slate-700">{generatedData.competitive_intelligence.competitive_salary}</p>
                        </div>
                      )}
                      
                      {/* Unique Perks & Benefits */}
                      {generatedData.competitive_intelligence.unique_perks && generatedData.competitive_intelligence.unique_perks.length > 0 && (
                        <div className="mb-4">
                          <h4 className="font-semibold text-xs text-slate-900 mb-2">Unique Perks & Benefits</h4>
                          <ul className="list-disc list-inside space-y-1">
                            {generatedData.competitive_intelligence.unique_perks.map((perk, i) => (
                              <li key={i} className="text-xs text-slate-700">{perk}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Market Analysis Section */}
                  {generatedData.market_data && (
                    <div className="pt-4 border-t">
                      <h3 className="font-semibold text-sm text-slate-900 mb-3">Market Analysis</h3>
                      <p className="text-xs text-slate-600 mb-3">Salary insights and market data from multiple sources</p>
                      
                      {/* Salary Analysis */}
                      {generatedData.market_data.salary_analysis && generatedData.market_data.salary_analysis.length > 0 && (
                        <div className="mb-4">
                          <h4 className="font-semibold text-xs text-slate-900 mb-2">Salary Analysis by Source</h4>
                          <div className="space-y-2">
                            {generatedData.market_data.salary_analysis.map((source, i) => (
                              <div key={i} className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                                <div className="flex items-center justify-between mb-1">
                                  <span className="font-medium text-xs text-slate-900">{source.source}</span>
                                  <span className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded">{source.confidence}</span>
                                </div>
                                <p className="text-xs text-slate-700">{source.range}</p>
                                {source.details && (
                                  <p className="text-xs text-slate-500 mt-1">{source.details}</p>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Data Sources */}
                      {generatedData.market_data.sources_scraped && generatedData.market_data.sources_scraped.length > 0 && (
                        <div className="mb-4">
                          <h4 className="font-semibold text-xs text-slate-900 mb-2">Data Sources</h4>
                          <div className="flex flex-wrap gap-2">
                            {generatedData.market_data.sources_scraped.map((source, i) => (
                              <span key={i} className="text-xs px-3 py-1 bg-emerald-50 text-emerald-700 rounded-full border border-emerald-200">
                                {source}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Market Insights */}
                      {generatedData.market_data.insights && (
                        <div>
                          <h4 className="font-semibold text-xs text-slate-900 mb-2">Market Insights</h4>
                          <p className="text-xs text-slate-700">{generatedData.market_data.insights}</p>
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
                </div>
              </div>
            )}

            {/* Show image when ready */}
            {outputImage ? (
              <Card>
                <CardHeader>
                  <CardTitle>Your Generated Requisition</CardTitle>
                </CardHeader>
                <CardContent>
                  <div 
                    className="relative"
                    onContextMenu={(e) => e.preventDefault()}
                  >
                    <img 
                      src={outputImage} 
                      alt="Generated Requisition" 
                      className="w-full border border-slate-200 rounded pointer-events-none"
                      style={{ 
                        userSelect: 'none',
                        WebkitUserSelect: 'none',
                        MozUserSelect: 'none',
                        msUserSelect: 'none',
                        WebkitTouchCallout: 'none',
                        pointerEvents: 'none'
                      }}
                      draggable="false"
                      onDragStart={(e) => e.preventDefault()}
                    />
                    {/* Transparent overlay to prevent interaction */}
                    <div 
                      className="absolute inset-0 bg-transparent cursor-default"
                      onContextMenu={(e) => e.preventDefault()}
                      onDragStart={(e) => e.preventDefault()}
                    />
                    {/* Watermark */}
                    <div className="absolute bottom-4 right-4 bg-white/90 px-3 py-1.5 rounded-lg shadow-sm border border-slate-200">
                      <p className="text-xs text-slate-500 font-medium">
                        SmartReqs Preview • <span className="text-blue-600">Sign up to export</span>
                      </p>
                    </div>
                  </div>
                </CardContent>
                <CardContent className="pt-4 border-t">
                  <div className="flex gap-3">
                    <Button
                      onClick={() => navigate("/pricing")}
                      variant="default"
                      className="flex-1"
                    >
                      <UserPlus className="w-4 h-4 mr-2" />
                      Sign Up to Save
                    </Button>
                    <Button
                      onClick={() => {
                        setGeneratedData(null);
                        setOutputImage(null);
                        setFormData({
                          requisition_name: "",
                          job_title: "",
                          company_brand: "",
                          basic_qualifications: "",
                          preferred_qualifications: "",
                          education: "",
                          years_experience: "",
                          location: "",
                          job_type: "",
                          industry: "",
                          tone: "",
                          company_culture: "",
                          additional_context: "",
                          travel: "",
                          relocation_provided: "",
                          us_citizenship: [],
                          security_clearance: "",
                          shift_coverage: "",
                          benefits_offered: []
                        });
                      }}
                      variant="outline"
                      className="flex-1"
                    >
                      Try Another
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ) : isCapturing ? (
              <Card>
                <CardHeader>
                  <CardTitle>Preparing Your Requisition...</CardTitle>
                </CardHeader>
                <CardContent className="text-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-900 mx-auto mb-4"></div>
                  <p className="text-slate-600">Converting to image format...</p>
                </CardContent>
              </Card>
            ) : !generatedData ? (
              <Card className="flex flex-col flex-1 h-full items-center justify-center">
                <CardContent className="text-center py-12">
                  <Sparkles className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-slate-900 mb-2">Ready to Generate</h3>
                  <p className="text-slate-600">
                    Fill in the form on the left and click "Generate Requisition" to see AI in action!
                  </p>
                </CardContent>
              </Card>
            ) : null}

            {/* Decorative Image - Shows at bottom after generation OR before generation */}
            {!loading && (
              <div className="mt-6">
                <img
                  src="/creative-workspace.jpg"
                  alt="Creative Workspace"
                  className="w-full h-auto object-contain rounded-lg shadow-lg"
                />
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Premium Feature Modal */}
      <Dialog open={showPremiumModal} onOpenChange={setShowPremiumModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <Crown className="w-6 h-6 text-amber-500" />
              Enhanced Research - Premium Feature
            </DialogTitle>
            <DialogDescription className="text-slate-600">
              Unlock powerful AI-driven competitive intelligence for your job requisitions
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <p className="text-sm text-slate-700">
              Enhanced Research is available exclusively to subscribed users. When enabled, you'll get:
            </p>
            
            <div className="space-y-3">
              <div className="flex items-start gap-3 p-3 bg-blue-50 rounded-lg">
                <Check className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-semibold text-sm text-blue-900">Real-Time Competitive Intelligence</p>
                  <p className="text-xs text-blue-700">Live salary data aggregated from Indeed, Glassdoor, LinkedIn, and more</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3 p-3 bg-emerald-50 rounded-lg">
                <Check className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-semibold text-sm text-emerald-900">Industry-Specific Skills Analysis</p>
                  <p className="text-xs text-emerald-700">Top in-demand skills extracted from current job postings in your industry</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3 p-3 bg-purple-50 rounded-lg">
                <Check className="w-5 h-5 text-purple-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-semibold text-sm text-purple-900">Common Requirements & Qualifications</p>
                  <p className="text-xs text-purple-700">See what competitors are requiring for similar positions</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3 p-3 bg-amber-50 rounded-lg">
                <Check className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-semibold text-sm text-amber-900">Authoritative Source Research</p>
                  <p className="text-xs text-amber-700">AI searches O*NET, BLS, and professional standards for comprehensive job definitions</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3 p-3 bg-rose-50 rounded-lg">
                <Check className="w-5 h-5 text-rose-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-semibold text-sm text-rose-900">Market Perks & Benefits Insights</p>
                  <p className="text-xs text-rose-700">Discover what benefits competitors are offering to attract top talent</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3 p-3 bg-cyan-50 rounded-lg">
                <Check className="w-5 h-5 text-cyan-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-semibold text-sm text-cyan-900">Security Clearance Premium Calculations</p>
                  <p className="text-xs text-cyan-700">Automatic 25% salary premium for cleared positions based on market data</p>
                </div>
              </div>
            </div>
            
            <div className="pt-4 border-t border-slate-200">
              <p className="text-xs text-slate-500 mb-4 text-center">
                Join SmartReqs to access Enhanced Research and save unlimited requisitions
              </p>
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setShowPremiumModal(false)}
                >
                  Maybe Later
                </Button>
                <Button
                  className="flex-1 bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 text-white"
                  onClick={() => navigate("/pricing")}
                >
                  <Crown className="w-4 h-4 mr-2" />
                  View Plans
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
